import { useState, useContext } from "react";
import { Link } from "react-router-dom";
import { SidebarContext } from "../../../shared/context/SidebarContext";
import { fetchFunil, fetchCompras } from "../services/trackingServiceV2";
import { toast } from "react-toastify";
import DateFilterBar from "../components/DateFilterBar";
import JornadaModal, { UtmBadges } from "../components/JornadaModal";

// Funil por PRODUTO, em 3 passos (contrato do tracking):
//   Visitantes → Leads → Clicou no checkout (checkout_intent) → Chegou ao checkout (checkout + checkout_redirect) → Compras
// "Compras" do funil = vendas do produto no período cujo visitante passou pelas páginas do produto.
// O total do produto (rastreadas ou não) aparece ao lado, com link pras vendas sem tracking.

function formatR$(v) {
    return "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}
function pct(a, b) {
    if (!b || Number(b) === 0) return "0%";
    return ((a / b) * 100).toFixed(1) + "%";
}

export default function FunilConversao() {
    const { isOpen } = useContext(SidebarContext);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [compras, setCompras] = useState(null);
    const [ultimo, setUltimo] = useState(null); // { startDate, endDate, filters }
    const [jornadaVisitor, setJornadaVisitor] = useState(null);
    const [mostrarCompras, setMostrarCompras] = useState(false);

    async function handleBuscar(startDate, endDate, filters = {}) {
        setLoading(true);
        setUltimo({ startDate, endDate, filters });
        setMostrarCompras(false);
        try {
            const [funil, comprasRes] = await Promise.all([
                fetchFunil(startDate, endDate, filters),
                fetchCompras(startDate, endDate, filters),
            ]);
            setData(funil);
            setCompras(comprasRes);
        } catch {
            toast.error("Erro ao buscar funil");
        }
        setLoading(false);
    }

    const k = data?.kpis || {};
    const semTrackingHref = ultimo
        ? `/tracking/vendas-sem-tracking?start=${ultimo.startDate}&end=${ultimo.endDate}${ultimo.filters?.produto_id ? `&produto_id=${ultimo.filters.produto_id}` : ""}`
        : "/tracking/vendas-sem-tracking";

    const passos = data ? [
        { label: "Visitantes", value: k.visitantes_unicos, color: "text-blue-400", bar: "bg-blue-500" },
        { label: "Clicaram no checkout", value: k.total_intents, color: "text-yellow-400", bar: "bg-yellow-500" },
        { label: "Chegaram ao checkout", value: k.total_checkouts, color: "text-orange-400", bar: "bg-orange-500" },
        { label: "Compraram", value: k.total_vendas, color: "text-emerald-400", bar: "bg-emerald-500" },
    ] : [];
    const base = Math.max(1, Number(k.visitantes_unicos || 0));

    return (
        <div className="min-h-screen bg-surface-1 text-white flex">
            <main className={`transition-all duration-300 ${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full p-6 mb-16 sm:mb-0`}>
                <header className="mb-6">
                    <h1 className="text-3xl font-bold mb-1">Funil de Conversão</h1>
                    <p className="text-sm text-ink-3 mb-4">Por produto: da página ao checkout e à compra, só com o tracking novo.</p>
                    <DateFilterBar onBuscar={handleBuscar} loading={loading} />
                </header>

                {!data && !loading && <p className="text-center text-ink-4 py-8">Selecione um período e clique em Buscar</p>}
                {loading && <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" /></div>}

                {data && !loading && (
                    <div className="space-y-6">
                        {/* Funil */}
                        <div className="bg-surface-2 p-5 rounded-lg">
                            <div className="flex items-baseline justify-between mb-4">
                                <h2 className="text-lg font-semibold">Funil</h2>
                                <span className="text-xs text-ink-4">{k.total_pageviews} pageviews · <span className="text-cyan-400">{k.total_leads} leads</span> ({pct(k.total_leads, k.visitantes_unicos)} dos visitantes)</span>
                            </div>
                            <div className="space-y-3">
                                {passos.map((p, i) => {
                                    const anterior = i > 0 ? passos[i - 1].value : null;
                                    const largura = Math.max(2, Math.min(100, (Number(p.value || 0) / base) * 100));
                                    return (
                                        <div key={p.label} className="grid grid-cols-[180px_1fr_120px] items-center gap-3">
                                            <div>
                                                <p className="text-xs text-ink-3">{p.label}</p>
                                                <p className={`text-xl font-bold ${p.color}`}>{p.value ?? 0}</p>
                                            </div>
                                            <div className="h-7 bg-surface-3 rounded overflow-hidden">
                                                <div className={`h-full ${p.bar} rounded transition-all`} style={{ width: `${largura}%` }} />
                                            </div>
                                            <div className="text-right text-xs text-ink-3">
                                                {i > 0 && <span>{pct(p.value, anterior)} do passo anterior</span>}
                                                {i > 1 && <span className="block text-ink-4">{pct(p.value, k.visitantes_unicos)} dos visitantes</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Receita e totais do produto */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-surface-2 p-4 rounded-lg">
                                <p className="text-xs text-ink-3">Compras rastreadas</p>
                                <p className="text-2xl font-bold text-emerald-400">{k.total_vendas}</p>
                                <p className="text-xs text-ink-4 mt-1">+ {k.total_orderbumps} orderbump(s)</p>
                            </div>
                            <div className="bg-surface-2 p-4 rounded-lg">
                                <p className="text-xs text-ink-3">Receita rastreada</p>
                                <p className="text-2xl font-bold text-emerald-300">{formatR$(k.valor_total)}</p>
                                <p className="text-xs text-ink-4 mt-1">{formatR$(k.receita_vendas)} principal · {formatR$(k.receita_orderbumps)} bumps</p>
                            </div>
                            <div className="bg-surface-2 p-4 rounded-lg">
                                <p className="text-xs text-ink-3">Total do produto no período</p>
                                <p className="text-2xl font-bold text-white">{k.vendas_produto_total}</p>
                                <p className="text-xs text-ink-4 mt-1">{formatR$(k.receita_produto_total)} com bumps · {pct(k.total_vendas, k.vendas_produto_total)} rastreadas</p>
                            </div>
                            <Link to={semTrackingHref} className="bg-amber-900/20 border border-amber-700/40 p-4 rounded-lg hover:bg-amber-900/30 transition block">
                                <p className="text-xs text-amber-300">Vendas sem tracking</p>
                                <p className="text-2xl font-bold text-amber-400">{data.vendas_sem_tracking?.total ?? 0}</p>
                                <p className="text-xs text-amber-300/70 mt-1">{formatR$(data.vendas_sem_tracking?.valor)} · ver lista →</p>
                            </Link>
                        </div>

                        {/* Compras do período */}
                        {compras && (
                            <div className="bg-surface-2 rounded-lg">
                                <button onClick={() => setMostrarCompras(v => !v)} className="w-full flex items-center justify-between p-5 text-left">
                                    <div>
                                        <h2 className="text-lg font-semibold">Compras do período</h2>
                                        <p className="text-xs text-ink-3">{compras.kpis.total} compra(s) · {compras.kpis.rastreadas} com jornada · clique numa compra pra ver as páginas que a pessoa visitou antes</p>
                                    </div>
                                    <span className="text-ink-3 text-sm">{mostrarCompras ? "▲" : "▼"}</span>
                                </button>
                                {mostrarCompras && (
                                    <div className="overflow-x-auto border-t border-white/[0.08]">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-white/[0.08] text-ink-3">
                                                    <th className="text-left p-3">Quando</th>
                                                    <th className="text-left p-3">Cliente</th>
                                                    <th className="text-left p-3">Produto</th>
                                                    <th className="text-left p-3">Valor</th>
                                                    <th className="text-left p-3">Bumps</th>
                                                    <th className="text-left p-3">Origem (último clique)</th>
                                                    <th className="text-left p-3">Jornada</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {compras.compras.map(c => (
                                                    <tr key={c.transacao} className="border-b border-white/[0.08] hover:bg-surface-3/30">
                                                        <td className="p-3 text-ink-2 text-xs whitespace-nowrap">{c.data_aprovacao ? new Date(c.data_aprovacao).toLocaleString("pt-BR") : "—"}</td>
                                                        <td className="p-3">
                                                            <p className="text-sm">{c.cliente?.nome || "—"}</p>
                                                            <p className="text-[10px] text-ink-4">{c.cliente?.email || c.transacao}</p>
                                                        </td>
                                                        <td className="p-3 text-xs text-ink-2 max-w-[180px] truncate">{c.nome_produto}</td>
                                                        <td className="p-3 text-emerald-400 font-medium whitespace-nowrap">{formatR$(c.valor_total)}</td>
                                                        <td className="p-3 text-xs">{c.qtd_bumps > 0 ? <span className="bg-amber-900/40 text-amber-300 px-2 py-0.5 rounded-full">{c.qtd_bumps} · {formatR$(c.valor_bumps)}</span> : <span className="text-ink-5">—</span>}</td>
                                                        <td className="p-3"><UtmBadges utms={c.utms} /></td>
                                                        <td className="p-3">
                                                            {c.rastreada ? (
                                                                <button onClick={() => setJornadaVisitor(c.visitor_id)} className="text-emerald-400 hover:text-emerald-300 text-xs underline whitespace-nowrap">
                                                                    Ver jornada ({c.paginas_antes} pág.)
                                                                </button>
                                                            ) : c.visitor_id ? (
                                                                <button onClick={() => setJornadaVisitor(c.visitor_id)} className="text-ink-3 hover:text-white text-xs underline whitespace-nowrap" title="Nenhuma visita às páginas deste produto antes da compra (snippet antigo, outra página, ou visitou só depois)">
                                                                    Histórico (sem visita antes)
                                                                </button>
                                                            ) : <span className="text-ink-5 text-xs">sem visitante</span>}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {compras.compras.length === 0 && <tr><td colSpan={7} className="text-center text-ink-4 p-6">Nenhuma compra no período</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Evolução diária */}
                        {data.diario && data.diario.length > 0 && (
                            <div className="bg-surface-2 p-5 rounded-lg">
                                <h2 className="text-lg font-semibold mb-3">Evolução Diária</h2>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-white/[0.08] text-ink-3">
                                                <th className="text-left p-2">Data</th>
                                                <th className="text-left p-2">Visitantes</th>
                                                <th className="text-left p-2">Pageviews</th>
                                                <th className="text-left p-2">Leads</th>
                                                <th className="text-left p-2">Clicaram</th>
                                                <th className="text-left p-2">Chegaram</th>
                                                <th className="text-left p-2">Compras rastreadas</th>
                                                <th className="text-left p-2">Compras do produto</th>
                                                <th className="text-left p-2">Receita rastreada</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.diario.map(d => (
                                                <tr key={d.dia} className="border-b border-white/[0.08] hover:bg-surface-3/30">
                                                    <td className="p-2 text-ink-2 whitespace-nowrap">{new Date(d.dia).toLocaleDateString("pt-BR", { timeZone: "UTC" })}</td>
                                                    <td className="p-2">{d.visitantes}</td>
                                                    <td className="p-2">{d.pageviews}</td>
                                                    <td className="p-2">{d.leads}</td>
                                                    <td className="p-2">{d.intents}</td>
                                                    <td className="p-2">{d.checkouts}</td>
                                                    <td className="p-2 text-emerald-400">{d.vendas}</td>
                                                    <td className="p-2 text-ink-2">{d.vendas_total}</td>
                                                    <td className="p-2 text-emerald-300">{formatR$(d.valor)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {jornadaVisitor && <JornadaModal visitorId={jornadaVisitor} onClose={() => setJornadaVisitor(null)} />}
        </div>
    );
}
